import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/forms/components/PasswordInput'
import { PasswordStrengthBar } from '@/forms/components/PasswordStrengthBar'
import { usePasswordStrength } from '@/hooks/usePasswordStrength'
import API from '@/lib/api'
import { passwordMinLength } from '@/lib/crypto/passwordMinLength'

interface SetPasswordFormProps {
  action: string
  code: string | null
  email: string | null
  onSuccess?: (response: unknown) => void
  simple?: boolean
}

interface SetPasswordFormValues {
  code: string | null
  email: string | null
  password: string
  passwordConfirmation: string
}

const resolver = async (values: SetPasswordFormValues) => {
  const errors: Record<string, { type: string; message: string }> = {}

  if (values.password.length < passwordMinLength) {
    errors.password = {
      type: 'length',
      message: `Heslo musí mít alespoň ${passwordMinLength} znaků`,
    }
  }

  if (values.password !== values.passwordConfirmation) {
    errors.passwordConfirmation = {
      type: 'mismatch',
      message: 'Hesla se neshodují',
    }
  }

  if (
    values.email &&
    values.email !== '' &&
    values.password.indexOf(values?.email) !== -1
  ) {
    errors.password = {
      type: 'email',
      message: 'Heslo nesmí obsahovat e-mail',
    }
  }

  return {
    values,
    errors,
  }
}

export const SetPasswordForm = ({
  action,
  code,
  email,
  onSuccess,
  simple,
}: SetPasswordFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    watch,
  } = useForm<SetPasswordFormValues>({
    resolver,
    defaultValues: {
      code: code || '',
      email,
    },
  })
  const password = watch('password')
  const passwordStrengthResult = usePasswordStrength(password || '')

  const onSubmit = (values: SetPasswordFormValues) => {
    if (isSubmitting) return
    if (Object.keys(errors).length > 0) return
    if (!values.password || !values.passwordConfirmation) return

    if (values.password !== values.passwordConfirmation) {
      return toast.error('Hesla se neshodují', {
        description: 'Zadejte prosím stejné heslo do obou polí.',
        duration: 5000,
        closeButton: true,
      })
    }

    setIsSubmitting(true)

    API.post(action, {
      email: values.email,
      token: values.code,
      password: values.password,
    })
      .then((response) => {
        setIsSubmitting(false)
        onSuccess?.(response)
      })
      .catch((error) => {
        setIsSubmitting(false)
        console.error(error)
        return error
      })
  }

  return (
    <form
      method="POST"
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 min-w-[300px]"
    >
      <input type="hidden" {...register('code')} />
      {email && <input type="hidden" {...register('email')} />}

      {!simple && (
        <div className="space-y-2">
          <Label htmlFor="code">Kód z e-mailu</Label>
          <InputOTP maxLength={6} {...register('code')} onChange={(value) => setValue('code', value)}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
              </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          {errors.code && (
            <p className="text-sm text-destructive">{errors.code.message}</p>
          )}
        </div>
      )}

      {!email && !simple && (
        <div className="space-y-2">
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type={email ? 'hidden' : 'email'}
            placeholder="E-mail"
            disabled={!!email || isSubmitting}
            readOnly={!!email}
            autoComplete="email"
            {...register('email', {
              required: 'Zadejte prosím e-mail',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Zadejte platbou e-mailovou adresu',
              },
            })}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="password">Nové heslo</Label>
        <PasswordInput
          id="password"
          placeholder="Nové heslo"
          disabled={isSubmitting}
          autoComplete="new-password"
          minLength={passwordMinLength}
          {...register('password', {
            required: 'Zadejte prosím nové heslo',
          })}
        />
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password.message}</p>
        )}
        <PasswordStrengthBar score={passwordStrengthResult?.score} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="passwordConfirmation">Potvrzení nového hesla</Label>
        <PasswordInput
          id="passwordConfirmation"
          placeholder="Potvrzení nového hesla"
          disabled={isSubmitting}
          autoComplete="new-password"
          {...register('passwordConfirmation', {
            required: 'Zadejte prosím potvrzení nového hesla',
            validate: (value) =>
              value === watch('password') || 'Hesla se neshodují',
          })}
        />
        {errors.passwordConfirmation && (
          <p className="text-sm text-destructive">
            {errors.passwordConfirmation.message}
          </p>
        )}
      </div>

      <Button className="w-full" disabled={isSubmitting} type="submit">
        {isSubmitting ? 'Nastavování hesla...' : 'Nastavit heslo'}
      </Button>
    </form>
  )
}
